import React from 'react';

const SdrRecordDetails = ({ record, onEdit, onDelete, onBack }) => {
  const getStatusColor = (status) => {
    const colors = {
      'draft': { bg: '#e5e7eb', text: '#374151', label: '已儲存' },
      'pending': { bg: '#fef3c7', text: '#92400e', label: '待批准' },
      'approved': { bg: '#d1fae5', text: '#065f46', label: '已批准' }
    };
    return colors[status] || colors['draft'];
  };

  const getLeaveTypeIcon = (leaveType) => {
    const icons = {
      '進修培訓': '📚',
      '公幹': '✈️',
      '事假': '🏠',
      '病假': '🏥',
      '會議': '👥'
    };
    return icons[leaveType] || '📋';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayNames = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const dayName = dayNames[date.getDay()];
    
    return `${year}年${month}月${day}日 (${dayName})`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const getImpactStats = () => {
    if (!record.affected_lessons || !Array.isArray(record.affected_lessons)) {
      return { totalLessons: 0, totalStudents: 0, uniqueClasses: 0 };
    }

    const totalLessons = record.affected_lessons.length;
    const totalStudents = record.affected_lessons.reduce((sum, lesson) => sum + (lesson.student_count || 0), 0);
    const uniqueClasses = new Set(record.affected_lessons.map(lesson => lesson.class_name)).size;
    
    return { totalLessons, totalStudents, uniqueClasses };
  };

  const statusColor = getStatusColor(record.status);
  const stats = getImpactStats();

  return (
    <div className="sdr-record-details">
      <div className="details-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回日曆
        </button>
        <h2>SDR 記錄詳情</h2>
        <div className="header-actions">
          <button className="edit-btn" onClick={() => onEdit(record)}>
            ✏️ 編輯記錄
          </button>
          <button 
            className="delete-btn" 
            onClick={() => {
              if (window.confirm('確定要刪除這個SDR記錄嗎？')) {
                onDelete(record.id);
              }
            }}
          >
            🗑️ 刪除記錄
          </button>
        </div>
      </div>

      <div className="details-content">
        {/* 基本資訊區塊 */}
        <div className="info-section">
          <h3>基本資訊</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">SDR編號:</span>
              <span className="info-value">{record.sdr_number}</span>
            </div>
            <div className="info-item">
              <span className="info-label">教師姓名:</span>
              <span className="info-value">{record.teacher_name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">所屬部門:</span>
              <span className="info-value">{record.department}</span>
            </div>
            <div className="info-item">
              <span className="info-label">事件代碼:</span>
              <span className="info-value">{record.event_code || '未設定'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">請假類型:</span>
              <span className="info-value">
                <span className="leave-type-icon">{getLeaveTypeIcon(record.leave_type)}</span>
                {record.leave_type}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">記錄狀態:</span>
              <span 
                className="status-badge"
                style={{ 
                  backgroundColor: statusColor.bg,
                  color: statusColor.text
                }}
              >
                {statusColor.label}
              </span>
            </div>
          </div>
        </div>

        {/* 活動詳情區塊 */}
        <div className="info-section">
          <h3>活動詳情</h3>
          <div className="info-grid">
            <div className="info-item full-width">
              <span className="info-label">活動描述:</span>
              <div className="info-value description">
                {record.activity_description}
              </div>
            </div>
            {record.detailed_reason && (
              <div className="info-item full-width">
                <span className="info-label">詳細原因:</span>
                <div className="info-value description">
                  {record.detailed_reason}
                </div>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">開始日期:</span>
              <span className="info-value">{formatDate(record.start_date)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">結束日期:</span>
              <span className="info-value">{formatDate(record.end_date)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">開始時間:</span>
              <span className="info-value">{formatTime(record.start_time)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">結束時間:</span>
              <span className="info-value">{formatTime(record.end_time)}</span>
            </div>
          </div>
        </div>

        {/* 影響統計區塊 */}
        <div className="info-section">
          <h3>影響統計</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📚</div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalLessons}</div>
                <div className="stat-label">影響課節數</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalStudents}</div>
                <div className="stat-label">影響學生人數</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏫</div>
              <div className="stat-content">
                <div className="stat-number">{stats.uniqueClasses}</div>
                <div className="stat-label">涉及班級數</div>
              </div>
            </div>
          </div>
        </div>

        {/* 代課安排表區塊 */}
        {record.affected_lessons && record.affected_lessons.length > 0 && (
          <div className="info-section">
            <h3>代課安排表</h3>
            <div className="substitute-table">
              <div className="table-header">
                <div className="header-cell">課節時段</div>
                <div className="header-cell">科目名稱</div>
                <div className="header-cell">班級資訊</div>
                <div className="header-cell">教室位置</div>
                <div className="header-cell">代課老師</div>
                <div className="header-cell">安排狀態</div>
              </div>
              <div className="table-body">
                {record.affected_lessons.map((lesson, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell">
                      <div className="period-info">
                        <div className="period-name">{lesson.period_name}</div>
                        <div className="period-time">{lesson.time}</div>
                      </div>
                    </div>
                    <div className="table-cell">
                      <span className="subject-name">{lesson.subject}</span>
                    </div>
                    <div className="table-cell">
                      <div className="class-info">
                        <div className="class-name">{lesson.class_name}</div>
                        <div className="student-count">{lesson.student_count}人</div>
                      </div>
                    </div>
                    <div className="table-cell">
                      <span className="classroom">{lesson.classroom}</span>
                    </div>
                    <div className="table-cell">
                      <span className="substitute-teacher">{lesson.substitute_teacher}</span>
                    </div>
                    <div className="table-cell">
                      <span className={`status-badge ${lesson.status === 'assigned' ? 'assigned' : 'pending'}`}>
                        {lesson.status === 'assigned' ? '已安排' : '待安排'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 記錄資訊區塊 */}
        <div className="info-section">
          <h3>記錄資訊</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">建立時間:</span>
              <span className="info-value">
                {new Date(record.created_at).toLocaleString('zh-TW')}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">最後更新:</span>
              <span className="info-value">
                {new Date(record.updated_at).toLocaleString('zh-TW')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SdrRecordDetails;
